/**
 * CSAT Survey Module
 *
 * Handles post-chat customer satisfaction surveys
 * Supports: stars (1-5), thumbs (up/down), NPS (0-10)
 */

/**
 * CSAT state object
 */
export function createCsatState(type = 'stars') {
    return {
        rating: null,
        type: type,
        submitted: false
    };
}

/**
 * Check if CSAT survey should be shown
 * @param {Object} tenantConfig - Tenant configuration
 * @param {Object} csatState - Current CSAT state
 * @returns {boolean}
 */
export function shouldShowCsatSurvey(tenantConfig, csatState) {
    const formsConfig = tenantConfig?.forms;
    return formsConfig?.postchat_enabled && !csatState?.submitted;
}

/**
 * Configure the CSAT UI based on survey type
 * @param {Object} elements - CSAT DOM elements
 * @param {string} type - Survey type (stars, thumbs, nps)
 */
export function configureCsatType(elements, type) {
    // Hide all rating types first
    if (elements.csatStars) elements.csatStars.style.display = 'none';
    if (elements.csatThumbs) elements.csatThumbs.style.display = 'none';
    if (elements.csatNps) elements.csatNps.style.display = 'none';
    if (elements.csatNpsLabels) elements.csatNpsLabels.style.display = 'none';

    // Show the appropriate rating type
    switch (type) {
        case 'stars':
        case 'csat':
            if (elements.csatStars) elements.csatStars.style.display = 'flex';
            break;
        case 'thumbs':
            if (elements.csatThumbs) elements.csatThumbs.style.display = 'flex';
            break;
        case 'nps':
            if (elements.csatNps) elements.csatNps.style.display = 'flex';
            if (elements.csatNpsLabels) elements.csatNpsLabels.style.display = 'flex';
            break;
    }

    // Clear any previous selections
    clearCsatSelections(elements);
}

/**
 * Clear all CSAT selections
 * @param {Object} elements - CSAT DOM elements
 */
export function clearCsatSelections(elements) {
    if (elements.csatStars) {
        elements.csatStars.querySelectorAll('.fyncall-csat-star').forEach(star => {
            star.classList.remove('active');
            star.textContent = '☆';
        });
    }
    if (elements.csatThumbs) {
        elements.csatThumbs.querySelectorAll('.fyncall-csat-thumb').forEach(thumb => {
            thumb.classList.remove('active');
        });
    }
    if (elements.csatNps) {
        elements.csatNps.querySelectorAll('.fyncall-csat-nps-btn').forEach(btn => {
            btn.classList.remove('active');
        });
    }
}

/**
 * Show the CSAT survey panel
 * @param {Object} elements - CSAT DOM elements
 * @param {Object} formsConfig - Forms configuration
 * @returns {Object} Initial CSAT state
 */
export function showCsatSurvey(elements, formsConfig) {
    if (!formsConfig || !formsConfig.postchat_enabled) {
        console.log('[FyncallCSAT] Post-chat survey not enabled');
        return null;
    }

    // Create initial state
    const state = createCsatState(formsConfig.postchat_type || 'stars');

    // Configure the survey based on type
    configureCsatType(elements, state.type);

    // Set the question text
    const question = formsConfig.postchat_question || 'How would you rate your experience?';
    if (elements.csatQuestion) {
        elements.csatQuestion.textContent = question;
    }

    // Configure follow-up
    if (elements.csatFollowup) {
        if (formsConfig.postchat_follow_up) {
            elements.csatFollowup.style.display = 'block';
            if (elements.csatFollowupLabel) {
                elements.csatFollowupLabel.textContent = formsConfig.postchat_follow_up_question || 'Any additional feedback? (optional)';
            }
        } else {
            elements.csatFollowup.style.display = 'none';
        }
    }

    // Configure skip button
    if (elements.csatSkip) {
        if (formsConfig.postchat_skip_enabled !== false) {
            elements.csatSkip.style.display = 'block';
            elements.csatSkip.textContent = formsConfig.postchat_skip_text || 'Skip';
        } else {
            elements.csatSkip.style.display = 'none';
        }
    }

    // Set thank you text
    if (elements.csatThankyouText) {
        elements.csatThankyouText.textContent = formsConfig.postchat_thank_you || 'Thank you for your feedback!';
    }

    // Show the survey panel
    if (elements.csatContent) elements.csatContent.style.display = 'flex';
    if (elements.csatThankyou) elements.csatThankyou.classList.remove('show');
    if (elements.csatActions) elements.csatActions.style.display = 'flex';
    if (elements.csatPanel) elements.csatPanel.classList.add('show');
    if (elements.csatSubmit) elements.csatSubmit.disabled = true;

    console.log('[FyncallCSAT] CSAT survey shown, type:', state.type);

    return state;
}

/**
 * Handle star rating selection
 * @param {Object} elements - CSAT DOM elements
 * @param {Object} state - CSAT state
 * @param {number} rating - Selected rating (1-5)
 */
export function handleStarRating(elements, state, rating) {
    state.rating = rating;
    if (elements.csatSubmit) elements.csatSubmit.disabled = false;

    // Update visual state
    if (elements.csatStars) {
        elements.csatStars.querySelectorAll('.fyncall-csat-star').forEach(star => {
            const starRating = parseInt(star.dataset.rating);
            if (starRating <= rating) {
                star.classList.add('active');
                star.textContent = '★';
            } else {
                star.classList.remove('active');
                star.textContent = '☆';
            }
        });
    }

    console.log('[FyncallCSAT] Star rating selected:', rating);
}

/**
 * Handle thumbs rating selection
 * @param {Object} elements - CSAT DOM elements
 * @param {Object} state - CSAT state
 * @param {string} rating - Selected rating ('up' or 'down')
 */
export function handleThumbsRating(elements, state, rating) {
    state.rating = rating;
    if (elements.csatSubmit) elements.csatSubmit.disabled = false;

    // Update visual state
    if (elements.csatThumbs) {
        elements.csatThumbs.querySelectorAll('.fyncall-csat-thumb').forEach(thumb => {
            thumb.classList.remove('active');
            if (thumb.dataset.rating === rating) {
                thumb.classList.add('active');
            }
        });
    }

    console.log('[FyncallCSAT] Thumbs rating selected:', rating);
}

/**
 * Handle NPS rating selection
 * @param {Object} elements - CSAT DOM elements
 * @param {Object} state - CSAT state
 * @param {number} rating - Selected rating (0-10)
 */
export function handleNpsRating(elements, state, rating) {
    state.rating = rating;
    if (elements.csatSubmit) elements.csatSubmit.disabled = false;

    // Update visual state
    if (elements.csatNps) {
        elements.csatNps.querySelectorAll('.fyncall-csat-nps-btn').forEach(btn => {
            btn.classList.remove('active');
            if (parseInt(btn.dataset.rating) === rating) {
                btn.classList.add('active');
            }
        });
    }

    console.log('[FyncallCSAT] NPS rating selected:', rating);
}

/**
 * Show the thank you message
 * @param {Object} elements - CSAT DOM elements
 * @param {Object} state - CSAT state
 * @param {Function} onClose - Callback when auto-close timer fires
 * @param {number} autoCloseDelay - Delay before auto-close (ms)
 */
export function showCsatThankYou(elements, state, onClose, autoCloseDelay = 3000) {
    if (elements.csatContent) elements.csatContent.style.display = 'none';
    if (elements.csatActions) elements.csatActions.style.display = 'none';
    if (elements.csatThankyou) elements.csatThankyou.classList.add('show');
    state.submitted = true;

    // Auto-close after delay
    if (onClose) {
        setTimeout(onClose, autoCloseDelay);
    }
}

/**
 * Close the CSAT survey
 * @param {Object} elements - CSAT DOM elements
 */
export function closeCsatSurvey(elements) {
    if (elements.csatPanel) {
        elements.csatPanel.classList.remove('show');
    }
    console.log('[FyncallCSAT] CSAT survey closed');
}

/**
 * Submit CSAT survey to backend
 * @param {Object} config - Widget configuration { baseUrl }
 * @param {string} sessionId - Session ID
 * @param {string} visitorToken - Visitor token
 * @param {Object} state - CSAT state
 * @param {string} feedback - Optional feedback text
 * @param {Function} getHeaders - Function to get request headers
 * @returns {Promise<boolean>} Success status
 */
export async function submitCsatSurvey(config, sessionId, visitorToken, state, feedback, getHeaders) {
    if (state.rating === null) return false;

    try {
        const response = await fetch(`${config.baseUrl}/api/v1/widget/sessions/${sessionId}/feedback`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({
                visitor_token: visitorToken,
                rating_type: state.type,
                rating: state.rating,
                feedback: feedback || null
            })
        });

        if (response.ok) {
            console.log('[FyncallCSAT] CSAT survey submitted successfully');
            return true;
        } else {
            console.warn('[FyncallCSAT] Failed to submit CSAT survey:', response.status);
            return false;
        }
    } catch (error) {
        console.error('[FyncallCSAT] Error submitting CSAT survey:', error);
        return false;
    }
}

/**
 * Setup CSAT event listeners
 * @param {Object} elements - CSAT DOM elements
 * @param {Object} state - CSAT state (will be mutated)
 * @param {Object} callbacks - Event callbacks { onSubmit, onSkip, onClose }
 */
export function setupCsatEvents(elements, state, callbacks = {}) {
    // Star rating clicks
    if (elements.csatStars) {
        elements.csatStars.querySelectorAll('.fyncall-csat-star').forEach(star => {
            star.onclick = () => handleStarRating(elements, state, parseInt(star.dataset.rating));
        });
    }

    // Thumbs rating clicks
    if (elements.csatThumbs) {
        elements.csatThumbs.querySelectorAll('.fyncall-csat-thumb').forEach(thumb => {
            thumb.onclick = () => handleThumbsRating(elements, state, thumb.dataset.rating);
        });
    }

    // NPS rating clicks
    if (elements.csatNps) {
        elements.csatNps.querySelectorAll('.fyncall-csat-nps-btn').forEach(btn => {
            btn.onclick = () => handleNpsRating(elements, state, parseInt(btn.dataset.rating));
        });
    }

    // Submit button
    if (elements.csatSubmit) {
        elements.csatSubmit.onclick = () => callbacks.onSubmit?.();
    }

    // Skip button
    if (elements.csatSkip) {
        elements.csatSkip.onclick = () => {
            closeCsatSurvey(elements);
            callbacks.onSkip?.();
        };
    }

    // Close button
    if (elements.csatClose) {
        elements.csatClose.onclick = () => {
            closeCsatSurvey(elements);
            callbacks.onClose?.();
        };
    }
}
